/**
 * Dédoublonnage des Exceptions d'une page : une erreur identique (même clé) part au plus une fois
 * par fenêtre de 10 s. La première part aussitôt ; les suivantes sont comptées, et la dernière
 * part à la fin de la fenêtre avec le nombre d'occurrences écartées : une erreur qui boucle ne
 * sature ni le réseau du visiteur ni le projet, et son volume reste connu.
 */

/** Fenêtre du dédoublonnage (cf. PRD). */
export const DEDUPE_WINDOW_MS = 10_000
/** Erreurs distinctes suivies au plus (mémoire bornée) : au-delà, la plus ancienne est oubliée. */
const MAX_TRACKED = 100

type Tracked<T> = {
  /** Dernière occurrence écartée, envoyée à la fin de la fenêtre. */
  latest: T | null
  /** Occurrences écartées depuis le dernier envoi. */
  count: number
  timer: ReturnType<typeof setTimeout>
}

export type Deduplicator<T> = {
  /** Une occurrence de l'erreur `key` : envoyée aussitôt, ou comptée. */
  offer(key: string, item: T): void
  /** Envoie les occurrences en attente (arrêt de l'enregistrement, fermeture de la page). */
  flush(): void
}

/**
 * `send(item, duplicateCount)` reçoit l'occurrence à envoyer et le nombre d'occurrences
 * identiques écartées depuis le dernier envoi (que celle-ci représente en plus d'elle-même).
 */
export function createDeduplicator<T>(
  send: (item: T, duplicateCount: number) => void,
  windowMs = DEDUPE_WINDOW_MS,
): Deduplicator<T> {
  const tracked = new Map<string, Tracked<T>>()

  /** Ouvre une fenêtre pour `key` : à sa fin, les occurrences écartées partent. */
  function open(key: string): Tracked<T> {
    const entry: Tracked<T> = {
      latest: null,
      count: 0,
      timer: setTimeout(() => close(key, entry), windowMs),
    }
    tracked.set(key, entry)
    return entry
  }

  function close(key: string, entry: Tracked<T>) {
    if (tracked.get(key) !== entry) return
    tracked.delete(key)
    if (entry.latest === null) return
    // L'erreur a continué : sa dernière occurrence part, et une nouvelle fenêtre commence.
    send(entry.latest, entry.count - 1)
    open(key)
  }

  return {
    offer(key, item) {
      const entry = tracked.get(key)
      if (entry) {
        entry.latest = item
        entry.count++
        return
      }
      if (tracked.size >= MAX_TRACKED) {
        const [oldestKey, oldest] = tracked.entries().next().value!
        clearTimeout(oldest.timer)
        tracked.delete(oldestKey)
        if (oldest.latest !== null) send(oldest.latest, oldest.count - 1)
      }
      open(key)
      send(item, 0)
    },

    flush() {
      for (const [key, entry] of [...tracked]) {
        clearTimeout(entry.timer)
        tracked.delete(key)
        if (entry.latest !== null) send(entry.latest, entry.count - 1)
      }
    },
  }
}
